import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, LifeBuoy } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { ListasBanner } from "./ListasBanner";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { ROLES, fmtFecha } from "../lib/format";

const SupportBanner = ({ soporte, onEnd }) => (
  <div className="bg-amber-500 text-slate-900 px-4 py-2 text-sm flex flex-wrap items-center gap-3" data-testid="support-banner">
    <LifeBuoy size={16} />
    <span>
      Acceso de soporte (solo lectura) a <b>{soporte.nombre_fantasia}</b> hasta {fmtFecha(soporte.expira)}
    </span>
    <Button size="sm" variant="outline" className="h-7 bg-white/80" onClick={onEnd} data-testid="support-end-button">
      Terminar acceso
    </Button>
  </div>
);

export const PanelLayout = ({ nav, subtitle }) => {
  const { me, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const items = nav.filter((n) => !n.roles || n.roles.includes(me.usuario.rol) || me.soporte);

  const salir = async () => {
    await logout();
    navigate("/ingresar");
  };
  const terminarSoporte = async () => {
    try {
      await api.delete("/admin/support-access");
      await refresh();
      navigate("/admin");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-[#1B2A41] text-slate-300 p-4 shrink-0 flex flex-col gap-6 md:min-h-screen">
        <div className="px-2 pt-2 space-y-2">
          <div className="bg-white rounded-lg px-3 py-2 inline-block"><BrandLogo className="h-7" testId="panel-logo" /></div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#E0A526]">{subtitle}</p>
        </div>
        <nav className="flex md:flex-col gap-1 overflow-x-auto" data-testid="sidebar-nav">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                  isActive ? "bg-slate-800 text-white" : "hover:bg-slate-800/60 hover:text-white"
                }`
              }
            >
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto border-t border-slate-800 pt-4 px-2 space-y-2">
          <p className="text-sm text-white truncate" data-testid="current-user-name">{me.usuario.nombre}</p>
          <p className="text-xs text-slate-400 truncate">{me.usuario.correo}</p>
          <p className="text-xs text-blue-300" data-testid="current-user-role">{ROLES[me.usuario.rol]}</p>
          <Button variant="ghost" size="sm" onClick={salir} data-testid="logout-button"
            className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white px-2">
            <LogOut size={16} className="mr-2" /> Cerrar sesión
          </Button>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        {me.soporte && <SupportBanner soporte={me.soporte} onEnd={terminarSoporte} />}
        <ListasBanner />
        <main className="flex-1 p-4 sm:p-6 lg:p-10 space-y-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
